/**
 * The open book, drawn — baskets as cards, chain clicks as rows.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE CARD PER TRADE, NOT PER STRATEGY
 *
 * Fire the same Short Straddle twice an hour apart and there are two cards.
 * They are two executions with two entry prices, two P&Ls and two exits, and
 * folding them into one row by name would recreate exactly the netting the
 * Trade grain exists to undo.
 *
 * MANUAL LEGS GO BELOW, ONE AFTER ANOTHER
 *
 * A chain click is a Trade with no strategy. It gets a row rather than a
 * card because there is no structure to draw — a card header announcing a
 * basket of one is ceremony around a single leg.
 *
 * THE BROKER IS ON EVERY TRADE
 *
 * The account selector in the header is for EXECUTION — it chooses where the
 * next order goes. This list is every account at once, so each card names
 * the one it was placed into, and the manual table carries it as a column.
 *
 * WHAT IS NOT HERE, AND WHY
 *
 * The mockup's P&L AUTO panel and the per-leg SL/TGT/TRAIL fields are still
 * absent. Nothing honours them yet: Guardian is Phase 7 Stage B. A
 * stop-loss field that arms nothing is worse than no field — it is a
 * promise the software does not keep, on the screen where believing it
 * costs money.
 *
 * Its ALL/½/⅓ exit sizing DOES exist now, as quarters, behind the sliders
 * button on a manual row — 5.15. It arrived once there was an exit path
 * that could size a fraction server-side; a percentage the browser resolved
 * into lots would have been the same empty promise.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useSyncExternalStore, memo, useEffect, useMemo, useState } from 'react'
import type { DragEvent as ReactDragEvent } from 'react'
import {
  AlertTriangle,
  GripVertical,
  LoaderCircle,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  X,
  Layers,
  Pencil,
} from 'lucide-react'
import {
  basketMtm,
  contractExpired,
  totalValue,
  type LegRole,
  type MtmLeg,
} from '@apna/shared'
import { cn } from '@/lib/utils'
import { PremiumProtectionBadge } from './PremiumProtectionBadge'
import { provisionalStore } from './provisionalStore'
import { useTickCell, useTickLtps } from '@/feed/useTick'
import { api } from '@/lib/api'
import { ProtectionInput } from '@/components/ProtectionInput'
import { compactInr } from './quantity'
import { canAdopt, canDetach, type DraggedLeg } from './legDrop'
import { LegRoleChip } from './LegRoleChip'
import { OpenRiskChip } from './OpenRiskChip'
import type { OpenRisk, ProtectionSpec } from '@apna/shared'
import { tradeName, type OpenBookLeg, type OpenBookTrade } from './useOpenBook'

/**
 * Dragging a leg from one trade to another — ROADMAP 9.9.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DRAWER OWNS THE DRAG, THE CARDS ONLY REPORT IT
 *
 * Which leg is in the hand has to be known by every drop target at once, so
 * it lives in the pane above these components rather than in any of them.
 * HTML5 drag-and-drop will not let a `dragover` handler read what is being
 * dragged — `dataTransfer` is deliberately opaque until the drop — so the
 * highlighting cannot be driven from the event and has to come from state.
 *
 * NOTHING HERE IS A PERMISSION CHECK. `canAdopt` decides what lights up;
 * the server re-reads inside its transaction and is free to refuse it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface LegDragApi {
  /** The leg currently in the hand, or null. */
  dragged: DraggedLeg | null
  onDragStart: (trade: OpenBookTrade, leg: OpenBookLeg) => void
  onDragEnd: () => void
  /**
   * Drop it here. Null detaches it back to the manual list; `intoName` is
   * what the confirmation calls the destination.
   */
  onDrop: (targetTradeId: string | null, intoName: string) => void
  /** The leg whose move is in flight. Its row stays put until it commits. */
  moving: string | null
  /** 9.10. Label a leg, or clear the label. */
  onRole: (tradeLegId: string, role: LegRole | null) => void
}

/**
 * Start a leg drag so the browser will actually deliver the drop.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * `setData` IS NOT OPTIONAL, AND ITS ABSENCE FAILS SILENTLY
 *
 * A drag with no payload still begins: the ghost image appears, `dragover`
 * fires, and a drop target still highlights. But Chrome resolves `dropEffect`
 * to `none` for an empty `dataTransfer`, and a drop with `dropEffect: none`
 * is never delivered — so releasing the mouse does nothing at all, with no
 * error anywhere. Firefox is stricter still and will not start the drag.
 *
 * The payload itself is unused: the leg in the hand is React state, because
 * `dataTransfer` is deliberately opaque during `dragover` and cannot be read
 * until the drop. The id is written only because a drag must carry something.
 *
 * Note for tests: jsdom's `fireEvent.drop` invokes the handler directly and
 * never consults `dropEffect`, so no jsdom test can catch this. It cost a
 * live trading session.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function beginLegDrag(
  e: ReactDragEvent,
  tradeLegId: string,
  onStart: () => void,
): void {
  /*
   * Guarded because a handler that THROWS here takes the drag with it and
   * says nothing — the same silent class of failure as the missing payload.
   * A real browser always supplies `dataTransfer` on dragstart.
   */
  if (e.dataTransfer) {
    e.dataTransfer.setData('text/plain', tradeLegId)
    e.dataTransfer.effectAllowed = 'move'
  }
  onStart()
}

/** Accept a drag here, and say it is a MOVE so the drop is delivered. */
function allowLegDrop(e: ReactDragEvent): void {
  e.preventDefault()
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
}

/**
 * What is wrong with ONE leg, if anything — ROADMAP 7.8.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A MARK ON THE ROW, NOT A REASON TO HIDE IT
 *
 * `orphanedQty` says our record and the broker's disagree about this
 * contract: we believe we hold it, their book does not list it. Usually
 * because it was squared off in their terminal.
 *
 * It used to take the whole TRADE out of the positions book and into a count
 * in the footer. That ghosted a position the user opened through this
 * software — it vanished from the one screen meant to show what they opened,
 * and nothing could be done with it from here.
 *
 * So the disagreement is a badge, on the one leg actually in dispute, and
 * everything else about the row keeps working. The exit button included: the
 * user may still act on a position they took, and the sizing comes from OUR
 * leg either way.
 *
 * EXPIRY WINS WHEN BOTH ARE TRUE, and on a live trade both usually are: the
 * broker drops an expired contract from its book, so the 7.8 sweep sees it
 * vanish and flags it. "The contract expired" is then the CAUSE and "the
 * broker stopped reporting it" merely how we found out — and a paper basket
 * has no broker at all, which is how this wording was got wrong once
 * already, sending a trader to check an account that was never involved.
 *
 * ASKED AT THE LEG, not the trade. The two inputs — `expiry` and
 * `orphanedQty` — are both leg columns, and every bug in this area so far
 * has been a trade-grain answer to a leg-grain question.
 * ─────────────────────────────────────────────────────────────────────────────
 */
type LegFlag = 'EXPIRED' | 'NOT_AT_BROKER'

function legFlag(leg: {
  netQty: number
  expiry: string
  orphanedQty: number | null
}): LegFlag | null {
  // A leg holding nothing is not in dispute with anyone: it is closed, or it
  // never opened, and the ORDERS book is where that story is.
  if (leg.netQty === 0) return null
  if (contractExpired(leg.expiry)) return 'EXPIRED'
  // `?? 0` and not `!== null`: an older build, a narrowed `select` or a test
  // fixture all deliver `undefined` here, and `undefined !== null` is true.
  return (leg.orphanedQty ?? 0) !== 0 ? 'NOT_AT_BROKER' : null
}

function LegFlagBadge({ leg }: { leg: OpenBookLeg }) {
  const flag = legFlag(leg)
  if (flag === null) return null
  const expired = flag === 'EXPIRED'
  return (
    <span
      className="flex shrink-0 items-center gap-0.5 rounded bg-warning/12 px-1 py-0.5 text-[8px] font-extrabold uppercase tracking-wide text-warning"
      data-testid={expired ? 'leg-expired' : 'leg-not-at-broker'}
      title={
        expired
          ? 'This contract expired — it settled at the exchange and cannot be exited. The position is shown because you opened it here; Portfolio → Review closes the record.'
          : `Your broker does not report ${Math.abs(leg.orphanedQty ?? 0)} of this contract — usually squared off in their terminal. We still show it because you opened it here. Exiting sends an order sized from our record; Portfolio → Review closes it without one.`
      }
    >
      <AlertTriangle size={9} />
      {expired ? 'EXPIRED' : 'NOT AT BROKER'}
    </span>
  )
}

/** How a trade's status reads, and whether it is a problem. */
const TRADE_STATUS: Record<
  string,
  { text: string; cls: string; title: string }
> = {
  OPENING: {
    text: 'FILLING',
    cls: 'bg-blue-500/12 text-blue-700 dark:text-blue-400',
    title: 'Legs are still being sent or filled.',
  },
  ISSUE: {
    text: 'INCOMPLETE',
    cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    title:
      'At least one leg never opened. The risk shown is the risk this basket ACTUALLY has, not the one it was designed for.',
  },
  CLOSING: {
    text: 'EXITING',
    cls: 'bg-blue-500/12 text-blue-700 dark:text-blue-400',
    title: 'An exit is in flight.',
  },
}

/** Both NEEDS_REVIEW headlines, chosen by the same rule the leg badges use. */
const REVIEW_STATUS: Record<
  LegFlag,
  { text: string; cls: string; title: string }
> = {
  EXPIRED: {
    text: 'CONTRACT EXPIRED',
    cls: 'bg-warning/15 text-warning',
    title:
      'A leg of this basket has expired — it settled at the exchange and cannot be exited. Portfolio → Review closes the record.',
  },
  NOT_AT_BROKER: {
    text: 'CHECK WITH BROKER',
    cls: 'bg-warning/15 text-warning',
    title:
      'Your broker does not report one or more of these legs — usually squared off in their terminal. They are still shown and still exitable; Portfolio → Review closes one without sending an order.',
  },
}

/**
 * The header badge for a trade, which must never contradict its own rows.
 *
 * NEEDS_REVIEW is the only status with two meanings, and the rows below
 * already decide between them per leg. So the header asks the SAME function
 * and reports the more specific answer, EXPIRED first — a trade with one
 * expired leg and one the broker dropped is an expiry story. A NEEDS_REVIEW
 * trade whose legs now flag nothing (the sweep's conclusion went stale, or
 * every disputed leg has since been closed) gets no badge at all rather than
 * a warning pointing at nothing.
 */
function tradeStatus(
  trade: OpenBookTrade,
): { text: string; cls: string; title: string } | undefined {
  if (trade.status !== 'NEEDS_REVIEW') return TRADE_STATUS[trade.status]
  const flags = new Set(trade.legs.map(legFlag))
  if (flags.has('EXPIRED')) return REVIEW_STATUS.EXPIRED
  if (flags.has('NOT_AT_BROKER')) return REVIEW_STATUS.NOT_AT_BROKER
  return undefined
}

/**
 * Is the trader closing this leg right now? — ROADMAP 9.26.
 *
 * Read straight from the singleton rather than threaded through four
 * components as a prop. The book will say `CLOSING` itself in about two
 * seconds — that is a broker round trip away and 9.25 measured it — and
 * until then this is the only thing that knows the click happened.
 *
 * It changes the BADGE and nothing else. The quantity and the P&L still
 * read what the leg actually holds, because it still holds it.
 */
function useClosing(tradeLegId: string): boolean {
  const snap = useSyncExternalStore(
    provisionalStore.subscribe,
    provisionalStore.getSnapshot,
    provisionalStore.getSnapshot,
  )
  return snap.closing.has(tradeLegId)
}

const LEG_STATUS: Record<string, { text: string; cls: string }> = {
  PENDING: { text: 'pending', cls: 'text-blue-600 dark:text-blue-400' },
  FAILED: { text: 'failed', cls: 'text-rose-600 dark:text-rose-400' },
  CLOSING: { text: 'exiting', cls: 'text-blue-600 dark:text-blue-400' },
}

/**
 * LIVE IS ORANGE, NOT RED — the mockup's `--c-live`, which is our `--warning`.
 *
 * Red is what a LOSS is, three columns to the right, and a mode badge that
 * borrows it reads as an alarm about the position rather than a statement
 * about the account. PAPER takes the violet already in our ramp
 * (`--chart-5`), matching the mockup's `--c-paper`.
 */
function ModePill({ mode }: { mode: string }) {
  return (
    <span
      data-testid="mode-pill"
      className={cn(
        'rounded px-1.5 py-0.5 text-[8px] font-extrabold tracking-widest',
        mode === 'LIVE'
          ? 'bg-warning/15 text-warning'
          : 'bg-[var(--tr-paper-bg)] text-[var(--tr-paper)]',
      )}
    >
      {mode}
    </span>
  )
}

/**
 * Which account this trade lives in. The NAME only.
 *
 * It used to carry a mode pill of its own, which printed LIVE twice in the
 * header — once for the trade and once for the account. They are the same
 * fact: `Trade.mode` is copied from the connection at placement and the
 * connection's mode is locked for its lifetime. So the header states it
 * once, from the trade, and `TradeCard` adds the account's own only in the
 * case where the two genuinely disagree — which would be a bug worth seeing
 * rather than a badge worth hiding.
 */
function BrokerChip({ label }: { label: string }) {
  return (
    <span
      /*
       * `inline-block`, and that is not cosmetic. `truncate` is
       * overflow-hidden plus an ellipsis, and NEITHER an overflow nor a
       * max-width applies to a plain inline box — so an account with a long
       * name ran straight out of the BROKER column and over the QTY beside
       * it. A block-level box is what gives the two properties something to
       * act on.
       */
      className="inline-block max-w-full truncate rounded bg-secondary px-1.5 py-0.5 align-middle text-[9px] font-semibold text-muted-foreground"
      title={`Placed into ${label}`}
      data-testid="broker-chip"
    >
      {label}
    </span>
  )
}

/**
 * The contract, spelled out — "NIFTY 19OCT 22950 CE".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WHOLE SYMBOL, AND THE STRIKE COLOURED BY SIDE OF THE CHAIN
 *
 * It used to print "22950 CE" alone. Two expiries of one strike are two
 * different contracts and a trader holds both in the same week, so a row
 * that names only the strike is unreadable at exactly the moment it matters
 * — and the drawer now lists every account and every expiry at once, which
 * makes that worse rather than better.
 *
 * The underlying and expiry are muted and the strike is not: the prefix is
 * context you scan past once per card, the strike is what you are looking
 * for. CE takes `--primary` and PE takes `--warning`, which are the
 * mockup's `--c-call` and `--c-live` — the same two colours the option
 * chain already uses for its two halves.
 * ─────────────────────────────────────────────────────────────────────────────
 */
/**
 * The quantity on a position row, SIGNED.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A SHORT READS AS A NEGATIVE NUMBER, because that is what it is
 *
 * This printed `Math.abs(netQty)`, so a short 65 and a long 65 rendered
 * identically and the only thing distinguishing them was the small S/B pill
 * at the far left of the row. On a straddle card — two legs, same strike,
 * same size, opposite sides — the two numbers were the same glyphs in the
 * same column, which is the one place a trader reads down rather than across.
 *
 * `netQty` is already signed: negative short, positive long. The abs() was
 * throwing away information the record already had.
 *
 * AN UNFILLED LEG HAS NO `netQty`, so the sign comes from the side it was
 * sent with — otherwise a refused SELL would print as a positive number
 * beside the filled shorts it was meant to join.
 */
const signedQty = (leg: OpenBookLeg): string => {
  const held = leg.netQty !== 0
  const size = held ? leg.netQty : leg.side === 'SELL' ? -leg.qty : leg.qty
  // `toLocaleString` puts the minus in front of the grouped digits for us.
  return size.toLocaleString('en-IN')
}

function Contract({ leg }: { leg: OpenBookLeg }) {
  const call = leg.optionType === 'CE'
  // "NIFTY 19OCT 22950 CE" -> prefix "NIFTY 19OCT", tail "22950 CE".
  const tail = `${Math.round(leg.strike)} ${leg.optionType}`
  const prefix = leg.symbol.endsWith(tail)
    ? leg.symbol.slice(0, leg.symbol.length - tail.length).trim()
    : leg.symbol

  return (
    <span className="num min-w-0 truncate" title={leg.symbol}>
      {prefix && (
        <span className="text-[10px] text-muted-foreground">{prefix} </span>
      )}
      <span className={cn('font-bold', call ? 'text-primary' : 'text-warning')}>
        {tail}
      </span>
    </span>
  )
}

/** B green, S red — the mockup's `--c-up` / `--c-down`, which are ours. */
function SidePill({ side }: { side: string }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded px-1 py-0.5 text-[8px] font-extrabold',
        side === 'BUY'
          ? 'bg-success/15 text-success'
          : 'bg-destructive/15 text-destructive',
      )}
    >
      {side === 'BUY' ? 'B' : 'S'}
    </span>
  )
}

/** HH:MM in IST, or an em dash on a leg that never filled. */
function fillTime(at: string | null): string {
  if (!at) return '—'
  return new Date(at).toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * `opened 3 Nov 2026, 10:05` — when this basket was put on.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EXPIRY CAME OFF, AND THE DATE WENT ON
 *
 * This used to lead with the first leg's expiry ("09 NOV · opened 12:41"),
 * which was wrong twice over. Every leg prints its own expiry in the table
 * two lines below, so the header was repeating it — and since 9.26 a basket
 * may legitimately span two, which made "the first leg's" a claim about the
 * whole card that is not true of it.
 *
 * What the header alone can say is WHEN THE POSITION WAS OPENED, and that
 * needed the date as much as the clock: a basket carried over a weekend read
 * "opened 12:41" with nothing to say it was Friday's.
 *
 * Written the way the expiry chips are — day, short month, year — so the two
 * dates on the card are read in one format rather than two.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function openedLine(trade: OpenBookTrade): string {
  const d = new Date(trade.openedAt)
  const on = d.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const at = d.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `opened ${on}, ${at}`
}

/**
 * The trade's unbooked P&L.
 *
 * `basketMtm` rather than a local sum: the Portfolio screen and the day
 * totals produce the same figure, and three implementations would be three
 * chances to differ about an unmarked leg. A partial sum is shown WITH its
 * count — on a four-legged condor with one quiet wing, saying nothing at all
 * about a position that is moving is the worse answer.
 */
function TradePnl({ legs }: { legs: readonly OpenBookLeg[] }) {
  const tokens = useMemo(
    () => legs.map((l) => l.token).filter((t): t is number => t !== null),
    [legs],
  )
  const ltps = useTickLtps(tokens)

  const { unrealised } = useMemo(
    () =>
      basketMtm(
        legs
          .filter((l) => l.netQty !== 0)
          .map(
            (l): MtmLeg => ({
              netQty: l.netQty,
              avgPrice: l.avgPrice,
              // Absent means no tick, which must stay distinct from zero all
              // the way into the sum.
              ltp: l.token === null ? null : (ltps.get(l.token) ?? null),
              realisedPnl: 0,
              chargesTotal: 0,
            }),
          ),
      ),
    [legs, ltps],
  )

  if (unrealised.kind === 'UNMARKED') {
    return (
      <span
        className="num text-[12px] text-muted-foreground"
        title={`No mark yet on ${unrealised.unmarked} leg${unrealised.unmarked === 1 ? '' : 's'}`}
        data-testid="trade-pnl-unmarked"
      >
        —
      </span>
    )
  }

  const value = totalValue(unrealised) ?? 0
  const partial = unrealised.kind === 'PARTIAL'

  return (
    <span className="flex shrink-0 items-center gap-1" data-testid="trade-pnl">
      {partial && (
        <span
          className="num rounded bg-warning/15 px-1 py-0.5 text-[8px] font-extrabold text-warning"
          title="Some legs have no mark yet. This covers only the ones that do."
        >
          {unrealised.marked}/{unrealised.marked + unrealised.unmarked}
        </span>
      )}
      <span
        className={cn(
          'num text-[13px] font-bold',
          partial && 'opacity-70',
          value >= 0 ? 'text-success' : 'text-destructive',
        )}
      >
        {value >= 0 ? '+' : '-'}₹{compactInr(Math.abs(value))}
      </span>
    </span>
  )
}

/** One leg's live price and P&L. Null when it has no mark. */
function useLegMark(leg: OpenBookLeg): {
  ltp: number | null
  pnl: number | null
} {
  const { value } = useTickCell(leg.token ?? 0, 'ltp')
  const ltp = leg.token !== null && value > 0 ? value : null
  return {
    ltp,
    // A leg that never filled has nothing to mark, and a zero here would read
    // as "flat on the day" rather than "never opened".
    pnl:
      ltp === null || leg.netQty === 0
        ? null
        : (ltp - leg.avgPrice) * leg.netQty,
  }
}

/**
 * Alignment belongs to the CELL, not to this.
 *
 * `text-right` on an inline span does nothing — it sets how text is aligned
 * INSIDE the box, and an inline box is only as wide as its text. The P&L
 * therefore rendered hard against the left of its column and touched the
 * right-aligned LTP beside it, reading as one merged number.
 */
const Money = ({ value }: { value: number | null }) => (
  <span
    className={cn(
      'num font-bold',
      value === null
        ? 'text-muted-foreground'
        : value >= 0
          ? 'text-success'
          : 'text-destructive',
    )}
  >
    {value === null
      ? '—'
      : `${value < 0 ? '-' : ''}₹${compactInr(Math.abs(value))}`}
  </span>
)

const LegRow = memo(function LegRow({
  leg,
  onExit,
  onTicket,
  busy,
  locked = false,
  drag,
  onDragStart,
}: {
  leg: OpenBookLeg
  onExit: () => void
  /** 9.29. The same partial-exit ticket a manual leg has had since 5.15. */
  onTicket?: (anchor: HTMLElement) => void
  busy: boolean
  locked?: boolean
  drag?: LegDragApi
  onDragStart?: () => void
}) {
  const { ltp, pnl } = useLegMark(leg)
  const held = leg.netQty !== 0
  // Lots are a SIZE, so this one stays unsigned — "2 lots" is right for a
  // short and "-2 lots" is not a thing anyone says.
  const lots =
    leg.lotSize > 0
      ? Math.abs(leg.netQty !== 0 ? leg.netQty : leg.qty) / leg.lotSize
      : null
  const closing = useClosing(leg.tradeLegId)
  const note = closing ? LEG_STATUS.CLOSING : LEG_STATUS[leg.status]

  /*
   * Only a held leg can be dragged, and not while anything is in flight. A
   * PENDING or FAILED row is in the basket precisely to be read, and picking
   * one up would offer a move the server refuses.
   */
  const draggable = Boolean(drag) && held && !busy && !locked
  const inHand = drag?.dragged?.tradeLegId === leg.tradeLegId

  return (
    <tr
      draggable={draggable}
      onDragStart={
        draggable && onDragStart
          ? (e) => beginLegDrag(e, leg.tradeLegId, onDragStart)
          : undefined
      }
      onDragEnd={draggable ? drag?.onDragEnd : undefined}
      className={cn(
        'group/leg border-b border-border/40 text-[11px] last:border-0',
        !held && 'opacity-70',
        inHand && 'opacity-40',
      )}
      data-testid={`trade-leg-${leg.tradeLegId}`}
    >
      {/*
              ── THE GRIP LEADS THE ROW — ROADMAP 9.9 ───────────────────────
              It lived inside the LEG cell, which was correct while LEG was
              the first column. The 2026-11-01 reorder put LEG third and the
              dots landed in the MIDDLE of the row, against the strike,
              reading as decoration on the contract rather than as a handle
              for the line.

              A column of its own is the only placement that survives a
              reorder: whatever the columns become, the handle is at the
              start of the row where a hand reaches for it. It holds its
              width at all times so nothing shifts sideways when the pointer
              enters — a column that moves under the cursor is how a
              mis-drag starts — and it is still the first thing shed below
              540px, where ~14px is worth more to the contract than to a
              gesture that has a drag target either way.
            */}
      <td className="tr-col-grip pl-2.5">
        {drag && (
          <span
            className={cn(
              'flex items-center transition-opacity',
              draggable
                ? 'cursor-grab text-muted-foreground opacity-0 group-hover/leg:opacity-60'
                : 'opacity-0',
            )}
            title={draggable ? 'Drag out to the manual list' : undefined}
            aria-hidden
          >
            <GripVertical size={11} />
          </span>
        )}
      </td>
      <td
        className="num text-right text-muted-foreground @max-[760px]/book:hidden"
        title="When this leg filled, IST"
      >
        {fillTime(leg.lastFillAt)}
      </td>
      <td className="py-1.5 pl-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <SidePill side={leg.side} />
          <Contract leg={leg} />
          {note && (
            <span
              className={cn(
                'shrink-0 text-[8.5px] font-bold uppercase tracking-wide',
                note.cls,
              )}
            >
              {note.text}
            </span>
          )}
          <LegFlagBadge leg={leg} />
          {drag && (
            <LegRoleChip
              /*
               * 9.58. After TIME, the chip is what carries the row from
               * 640 down to 560 — it sits INSIDE the LEG cell, so losing
               * its ~40px lowers that cell's floor rather than competing
               * with it. `tr-col-role` already drops it at 540 for the
               * manual table; the basket table needs it sooner because it
               * carries a TIME column the manual one does not.
               */
              className="tr-col-role @max-[640px]/book:hidden"
              role={leg.role}
              disabled={locked}
              testId={`leg-role-chip-${leg.tradeLegId}`}
              onChange={(role) => drag.onRole(leg.tradeLegId, role)}
            />
          )}
        </span>
      </td>
      <td className="num text-right text-muted-foreground">
        {/* A leg that never filled has no entry price, and 0.00 would read
            as a contract bought for nothing. */}
        {held ? leg.avgPrice.toFixed(2) : '—'}
      </td>
      <td
        className="num text-right text-muted-foreground"
        title={
          lots === null
            ? undefined
            : `${lots} lot${lots === 1 ? '' : 's'} of ${leg.lotSize}`
        }
      >
        {signedQty(leg)}
      </td>
      <td className="num text-right font-semibold">
        {ltp === null ? '—' : ltp.toFixed(2)}
      </td>
      <td className="pr-1 text-right">
        <Money value={pnl} />
      </td>
      <td className="whitespace-nowrap pr-2 text-right">
        <span className="inline-flex items-center justify-end gap-0.5">
          {/*
            ── 9.29. THE SAME TICKET A MANUAL LEG HAS ───────────────────────
            A leg inside a basket is no less partially exitable than a loose
            one — it is the same contract, the same holding and the same
            server path, and 5.15's fraction has always been per leg. The
            control was only ever wired to `ManualRow`, so a trader wanting
            half of one condor leg had to drag it out of the basket first.
          */}
          {onTicket && (
            <button
              type="button"
              onClick={(e) => onTicket(e.currentTarget)}
              disabled={busy || locked || !held}
              aria-label={`Exit part of ${leg.symbol}, or at a price`}
              data-testid={`exit-ticket-${leg.tradeLegId}`}
              title={
                held
                  ? 'Exit part of this leg, or at a price'
                  : 'This leg holds nothing'
              }
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-all hover:bg-primary/10 hover:text-primary disabled:opacity-30"
            >
              <SlidersHorizontal size={11} />
            </button>
          )}
          <button
            type="button"
            onClick={onExit}
            disabled={busy || locked || !held}
            aria-label={busy ? `Exiting ${leg.symbol}` : `Exit ${leg.symbol}`}
            aria-busy={busy}
            data-testid={`exit-leg-${leg.tradeLegId}`}
            title={held ? 'Exit this leg' : 'This leg holds nothing'}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive disabled:opacity-30"
          >
            {busy ? (
              <LoaderCircle size={12} className="animate-spin" />
            ) : (
              <X size={12} />
            )}
          </button>
        </span>
      </td>
    </tr>
  )
})

/**
 * Arming the legs of an open basket — ROADMAP 7.5.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BEHIND A TOGGLE, NOT IN THE ROW
 *
 * The mockup put SL and TGT inline on every position row. That was drawn at
 * one width; this drawer is draggable down to 30% of the workspace, where two
 * more inputs per row would crush the P&L — which is the column the pane
 * exists for. So the legs stay a dense table and the controls open under it.
 *
 * THE HEADER SAYS HOW MANY ARE ARMED, so the state is visible without
 * opening anything. An unarmed position looks unarmed from across the room,
 * which is the only property that matters here.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT SAVES ONE LEG AT A TIME, AND SAYS WHEN IT DID NOT
 *
 * Each field writes on blur rather than on keystroke — a PATCH per character
 * would race itself, and the last request to LAND is not the last one typed.
 * A refusal is shown against the panel rather than swallowed: a stop the
 * trader believes is set and is not is the exact failure 7.5 exists to stop.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function ProtectionPanel({
  legs,
  onSaved,
}: {
  legs: readonly OpenBookLeg[]
  onSaved: (
    tradeLegId: string,
    patch: { slSpec?: ProtectionSpec | null; tgtSpec?: ProtectionSpec | null },
  ) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  const save = async (
    leg: OpenBookLeg,
    patch: { sl?: ProtectionSpec | null; tgt?: ProtectionSpec | null },
  ): Promise<void> => {
    setSaving(leg.tradeLegId)
    setError(null)
    try {
      await api.patch(`/trades/legs/${leg.tradeLegId}/protection`, patch)
      onSaved(leg.tradeLegId, {
        ...(patch.sl === undefined ? {} : { slSpec: patch.sl }),
        ...(patch.tgt === undefined ? {} : { tgtSpec: patch.tgt }),
      })
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'The stop could not be saved.')
    } finally {
      setSaving(null)
    }
  }

  const held = legs.filter((l) => l.netQty !== 0)

  return (
    <div
      className="border-t border-border bg-secondary/20 px-2.5 py-2"
      data-testid="protection-panel"
    >
      <p className="mb-1.5 text-[9px] font-bold tracking-[0.18em] text-muted-foreground">
        STOP &amp; TARGET — RESTS AT THE BROKER
      </p>
      <div className="space-y-1">
        {held.map((l) => (
          <div key={l.tradeLegId} className="flex items-center gap-2">
            <span className="num min-w-0 flex-1 truncate text-[11px]">
              <span
                className={cn(
                  'font-bold',
                  l.optionType === 'CE' ? 'text-primary' : 'text-warning',
                )}
              >
                {Math.round(l.strike)} {l.optionType}
              </span>
            </span>
            <ProtectionInput
              label="SL"
              tone="sl"
              value={l.slSpec}
              testId={`arm-sl-${l.tradeLegId}`}
              onChange={(sl) => void save(l, { sl })}
            />
            <ProtectionInput
              label="TGT"
              tone="tgt"
              value={l.tgtSpec}
              testId={`arm-tgt-${l.tradeLegId}`}
              onChange={(tgt) => void save(l, { tgt })}
            />
            <span className="w-8 text-[9px] text-muted-foreground">
              {saving === l.tradeLegId ? 'saving' : ''}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[9.5px] leading-snug text-muted-foreground">
        Percent is of the entry price. The order is placed by the supervisor
        within a minute and rests at the exchange, so it survives this app being
        closed. Clearing a field stops it being replaced; it does not cancel one
        already resting.
      </p>
      {error && (
        <p
          className="mt-1 text-[10.5px] text-destructive"
          data-testid="protection-error"
        >
          {error}
        </p>
      )}
    </div>
  )
}

export interface TradeCardProps {
  /** 9.29. Partial exit of ONE leg inside the basket — the manual ticket. */
  onExitTicket?: (
    trade: OpenBookTrade,
    leg: OpenBookLeg,
    anchor: HTMLElement,
  ) => void
  /**
   * 9.36. Partial exit of the WHOLE basket.
   *
   * Takes the button that opened it, like the per-leg ticket: the panel is
   * anchored under the control rather than centred on a wide monitor.
   */
  onExitPart?: (trade: OpenBookTrade, anchor: HTMLElement) => void
  /**
   * 9.26. Present only on a basket the TRADER named — a chain combo or a
   * group they assembled. Absent on anything a strategy fired.
   */
  onRename?: (trade: OpenBookTrade) => void
  trade: OpenBookTrade
  /** Exit one leg of this trade. */
  onExitLeg: (trade: OpenBookTrade, leg: OpenBookLeg) => void
  /** Exit every held leg of this trade. */
  onExitTrade: (trade: OpenBookTrade) => void
  busy: boolean
  locked?: boolean
  exitingLegId?: string | null
  /** A leg's protection changed; the caller keeps the book current. */
  onArmed?: (
    tradeLegId: string,
    patch: { slSpec?: ProtectionSpec | null; tgtSpec?: ProtectionSpec | null },
  ) => void
  /** 9.9. Present when legs can be dragged in and out of this basket. */
  drag?: LegDragApi
  /**
   * 6.12. What THIS basket can lose today.
   *
   * Passed in rather than computed here: the drawer pools every basket's
   * legs per underlying for the headline figure, and a card working the
   * number out again from its own legs would be a second implementation of
   * the one calculation the header is already showing.
   *
   * Optional, so a card rendered anywhere without the hook simply omits the
   * chip rather than inventing a number.
   */
  risk?: OpenRisk
}

export function TradeCard({
  trade,
  onExitLeg,
  onExitTrade,
  onExitTicket,
  onExitPart,
  busy,
  locked = false,
  exitingLegId = null,
  onArmed,
  drag,
  risk,
  onRename,
}: TradeCardProps) {
  const [arming, setArming] = useState(false)
  const status = tradeStatus(trade)
  const held = trade.legs.some((l) => l.netQty !== 0)
  const heldLegs = trade.legs.filter((l) => l.netQty !== 0)
  const armed = heldLegs.filter(
    (l) => l.slSpec !== null || l.tgtSpec !== null,
  ).length

  /*
   * ONLY THE CARD UNDER THE CURSOR TURNS BLUE.
   *
   * `receiving` says this basket COULD take the leg; `over` says the cursor
   * is on it. Lighting every eligible basket at once — which is what this
   * did first — paints most of the drawer blue the moment a drag begins and
   * says nothing about where the leg will land. The highlight has to mean
   * "release here", not "this one is allowed".
   */
  const dragging = drag?.dragged ?? null
  const receiving = dragging !== null && canAdopt(dragging, trade)
  const [over, setOver] = useState(false)

  // A drag that ended anywhere else leaves `over` set, which would light this
  // card up at the START of the next one.
  useEffect(() => {
    if (dragging === null) setOver(false)
  }, [dragging])

  return (
    <section
      /*
       * `preventDefault` on dragover is what marks an element as a drop
       * target at all; `dropEffect` is what makes the browser DELIVER the
       * drop once it is. Both are set only when the basket can receive, so
       * an ineligible card shows the no-drop cursor instead of accepting and
       * then failing.
       *
       * Hover is tracked from `dragover` rather than `dragenter` because it
       * fires continuously: moving from one card to another corrects itself
       * without the enter/leave bookkeeping that child elements make noisy.
       */
      onDragOver={
        receiving
          ? (e) => {
              allowLegDrop(e)
              if (!over) setOver(true)
            }
          : undefined
      }
      onDragLeave={
        receiving
          ? (e) => {
              // Ignore the leave fired when the cursor crosses onto a CHILD.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                setOver(false)
            }
          : undefined
      }
      onDrop={
        receiving
          ? (e) => {
              e.preventDefault()
              setOver(false)
              drag?.onDrop(
                trade.tradeId,
                tradeName(trade) ?? `${trade.underlying} basket`,
              )
            }
          : undefined
      }
      className={cn(
        'group/card mb-2.5 overflow-hidden rounded-[10px] border transition-colors',
        receiving && over
          ? 'border-primary ring-1 ring-primary/40'
          : 'border-border',
      )}
      data-testid={`trade-${trade.tradeId}`}
      data-droppable={receiving ? 'true' : undefined}
      data-dropover={receiving && over ? 'true' : undefined}
    >
      {/*
        ── ONE LINE, IDENTITY LEFT, NUMBERS RIGHT ─────────────────────────
        This was `flex-wrap`, so once the risk chip arrived the header spilled
        onto a second row and the P&L — the number a trader looks at first —
        ended up underneath the name, separated from everything it belongs
        beside. The identity half now TRUNCATES instead of wrapping, which
        is the right thing to lose: the name is repeated in the legs below
        and the numbers are not repeated anywhere.
      */}
      <div className="flex items-center gap-x-2 border-b border-border bg-secondary/40 px-2.5 py-1.5">
        {/*
          THE NAME IS THE ELASTIC ONE, and it keeps a floor.
          As a plain shrinkable item it collapsed to nothing on a narrow
          drawer — the card lost its own title while the chips beside it
          stayed full width. `flex-1` makes it take the spare space, and the
          floor means it truncates to something readable rather than to
          nothing at all.
        */}
        <span
          className="min-w-[70px] shrink truncate text-[12px] font-bold"
          title={tradeName(trade) ?? `${trade.underlying} basket`}
        >
          {tradeName(trade) ?? `${trade.underlying} basket`}
        </span>
        {/*
          ── 9.26. RENAME, ONLY ON A NAME THE TRADER OWNS ─────────────────
          A trade a deployment opened is called after the strategy that
          fired it, and that word is the correspondence between the
          position, the journal, the deployment record and the strategy
          page. Editing it here would break that for one of the four, so
          the pencil simply is not drawn — `onRename` is absent. The server
          refuses it as well; this is the half that stops a trader finding
          a control that only says no.
        */}
        {onRename && (
          <button
            type="button"
            onClick={() => onRename(trade)}
            aria-label={`Rename ${tradeName(trade) ?? 'this position'}`}
            title="Rename this position"
            data-testid={`rename-trade-${trade.tradeId}`}
            className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/card:opacity-100 focus-visible:opacity-100"
          >
            <Pencil size={10} />
          </button>
        )}
        {/*
          ── OPEN RISK, BESIDE THE NAME ───────────────────────────────────
          It used to sit at the far right, after the account, the opened
          time and the P&L, where it read as one more trailing number and
          was the first thing an overflowing header pushed out of shape.
          Asked for on the left on 2026-11-01, and it belongs there: what a
          basket can lose is a property OF the basket, so it reads with the
          name rather than with the running totals.

          `flex-1` moves to this group so the spare space sits BETWEEN the
          identity and the numbers, instead of inside the name.
        */}
        {risk && (
          /*
            `overflow-hidden` is load-bearing — 9.34. A `min-w-0 flex-1`
            wrapper lets this group be squeezed to nothing, but a child
            that refuses to shrink still PAINTS outside it; that is how the
            risk chip ended up on top of the mode pill and the P&L. The
            chip now shrinks, and this clips whatever is still too wide
            rather than letting it overlap the numbers.
          */
          <span className="flex min-w-0 flex-1 items-center overflow-hidden">
            <OpenRiskChip risk={risk} testId={`trade-risk-${trade.tradeId}`} />
          </span>
        )}
        {!risk && <span className="flex-1" />}
        <span className="tr-hdr-acct shrink-0">
          <ModePill mode={trade.mode} />
        </span>
        {/*
          The account's own mode appears ONLY when it disagrees with the
          trade's. They are copied from one another at placement and the
          connection's mode is locked for life, so a difference is a real
          defect and worth seeing — while printing both every time was the
          "LIVE twice" the header used to show.
        */}
        {trade.broker.mode !== trade.mode && (
          <span
            className="shrink-0"
            title="This account's mode no longer matches the trade's."
          >
            {/* NOT sheddable: a mode mismatch is a defect, and the one time
                the pill matters is the time it must not be hidden. */}
            <ModePill mode={trade.broker.mode} />
          </span>
        )}
        {status && (
          <span
            className={cn(
              /*
                NOWRAP AND UNSHRINKABLE — 9.34. "CHECK WITH BROKER" was
                neither, so a tight header broke it across two lines and
                made the whole card a row taller than its neighbours. It
                is a warning about this basket's correctness; the risk
                chip beside it is the thing that may give up width, not
                this.
              */
              'shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[8px] font-extrabold tracking-widest',
              status.cls,
            )}
            title={status.title}
            data-testid="trade-status"
          >
            {status.text}
          </span>
        )}
        <span className="tr-hdr-acct min-w-0 shrink">
          <BrokerChip label={trade.broker.label} />
        </span>
        <PremiumProtectionBadge value={trade.premiumProtection} />
        <span className="tr-hdr-opened num shrink-0 whitespace-nowrap text-[9px] text-muted-foreground">
          {openedLine(trade)}
        </span>
        {/*
          LABELLED, because a bare "-56.6 K" beside the risk chip's bare
          "-₹44.6 K" was impossible to tell apart — and the two answer
          opposite questions: one is what has already happened, the other
          is what still can.
        */}
        <span className="flex shrink-0 items-center gap-1.5 text-[9.5px]">
          <span className="font-bold tracking-[0.12em] text-muted-foreground">
            P&amp;L
          </span>
          <TradePnl legs={trade.legs} />
        </span>
        {/*
          The armed count is on the header so an UNPROTECTED basket looks
          unprotected without opening anything — the only property that
          matters about a stop is whether it is there.
        */}
        {/*
          FROM HERE ON, NOTHING SHRINKS AND NOTHING IS SHED.
          The arm count and EXIT are the two controls on this card. The
          header used to overflow and push EXIT off the right-hand edge,
          leaving a position on screen with no way to close it — the same
          failure the manual table's exit cross had, and the same rule
          applies: context goes first, controls never.
        */}
        {held && !trade.premiumProtection && (
          <button
            type="button"
            onClick={() => setArming((a) => !a)}
            aria-expanded={arming}
            data-testid={`arm-toggle-${trade.tradeId}`}
            title={
              armed === 0
                ? 'No stop or target on this basket'
                : `${armed} of ${heldLegs.length} legs armed`
            }
            className={cn(
              'flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-[9px] font-extrabold transition-colors',
              armed === 0
                ? 'text-muted-foreground hover:text-foreground'
                : 'bg-success/12 text-success',
            )}
          >
            {armed === 0 ? <Shield size={11} /> : <ShieldCheck size={11} />}
            {armed}/{heldLegs.length}
          </button>
        )}
        {/*
          ── 9.36. THE SAME CONTROL AS A LEG'S, ONE LEVEL UP ──────────────
          This was a 25/50/75/100 strip that opened in the header, which
          was a second way of asking the question the leg's ticket already
          asks — a trader who had learned one had to learn the other to do
          the same thing to the whole basket.

          Now it is the same icon opening the same panel, with the basket
          as its subject. The panel drops its order type there: a resting
          price only means something against ONE contract, so a basket
          exits at market and the choice is simply absent.
        */}
        {onExitPart && held && (
          <button
            type="button"
            onClick={(e) => onExitPart(trade, e.currentTarget)}
            disabled={busy || locked}
            aria-label={`Exit part of ${tradeName(trade) ?? 'this basket'}`}
            title="Book part of this basket"
            data-testid={`trade-exit-part-${trade.tradeId}`}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-all hover:bg-primary/10 hover:text-primary disabled:opacity-40"
          >
            <SlidersHorizontal size={12} />
          </button>
        )}
        <button
          type="button"
          className="h-6 shrink-0 rounded bg-destructive/15 px-2.5 text-[10px] font-extrabold text-destructive transition-all hover:bg-destructive hover:text-white disabled:opacity-40"
          onClick={() => onExitTrade(trade)}
          disabled={busy || locked || !held}
          aria-busy={busy}
          data-testid={`trade-exit-all-${trade.tradeId}`}
          title={
            held
              ? 'Close every leg of this basket'
              : 'This basket holds nothing'
          }
        >
          {/*
            "EXIT", not "EXIT ALL". A basket is ONE position — a strategy or
            a combined premium — and the button closes that one thing. "ALL"
            is reserved for the manual block below, where it means something
            different and genuinely plural: every loose leg taken from the
            chain, which is the only place a trader has several independent
            positions under one button.
          */}
          {busy ? 'EXITING…' : 'EXIT'}
        </button>
      </div>

      {/*
        FIXED LAYOUT, so the numeric columns are the same width on every
        card. Left to the browser, each table sized its own columns from its
        own longest value and the ENTRY column landed in a different place
        on every basket — which is the one thing a stack of tables must not
        do, because the eye reads down a column and not across a row.
      */}
      <table className="w-full table-fixed">
        <thead>
          <tr className="border-b border-border text-[9px] tracking-wider text-muted-foreground">
            {/*
              TIME | ENTRY | LEG | QTY | LTP | P&L — the manual block's order
              below, minus BROKER, because a strategy is placed into one
              account and repeating it on every leg says nothing.

              10+12+34+10+13+16+5 = 100. They summed to 100 PLUS a fixed 28px
              column once, so `table-fixed` scaled every one of them and the
              two widest numbers ended up touching.

              LEG carries no width: it takes whatever the fixed shares leave,
              which is what keeps a contract name off its neighbours.
            */}
            <th className="tr-col-grip w-[3%] py-1" />
            {/*
              ── TIME SHEDS BEFORE THE CONTRACT DOES — ROADMAP 9.58 ───────
              The basket table had no shedding rule of its own, so below a
              760px drawer LEG was squeezed and `Contract` truncated from
              the RIGHT — dropping the strike and the option type, which is
              the half that identifies the contract. "NIFTY 09NOV …" names
              nothing a trader can act on.

              Measured, like the manual table's stages above, against the
              longest real name rather than by eye. BANKNIFTY is the worst
              case at 143px ("BANKNIFTY 25NOV 52500 PE", side pill and role
              chip beside it):

                with TIME     safe at 760, cut at 700
                without TIME  safe at 640, cut at 560

              So TIME goes at 760 and the row is safe to 640. It is the
              right thing to lose: every leg of a basket fills within
              seconds of its siblings, so the column is near-constant down
              the card, and the fill time is on the journal row in full.
            */}
            <th className="w-[10%] py-1 text-right font-semibold @max-[760px]/book:hidden">
              TIME
            </th>
            <th className="py-1 pl-3 text-left font-semibold">LEG</th>
            <th className="w-[12%] py-1 text-right font-semibold">ENTRY</th>
            <th className="w-[10%] py-1 text-right font-semibold">QTY</th>
            <th className="w-[13%] py-1 text-right font-semibold">LTP</th>
            <th className="w-[16%] py-1 pr-1 text-right font-semibold">
              P&amp;L
            </th>
            {/*
              ── A PIXEL WIDTH, NOT A PERCENTAGE — ROADMAP 9.57 ───────────
              The table is `table-fixed`, so a column is exactly what its
              header says and anything larger OVERFLOWS rather than
              widening it. This cell holds two 24px buttons, a 2px gap and
              8px of padding — 58px that does not scale — so at `w-[5%]`
              it fit only while the drawer was wider than 1,160px. Below
              that the cross spilled out and the card's `overflow-hidden`
              cut it off, with no scrollbar to say anything was missing.

              60px is that content plus a hair. The percentage columns
              beside it still sum under 100, so LEG — which carries no
              width and has the slack — absorbs the difference.
            */}
            <th className="w-[60px] py-1 pr-2" />
          </tr>
        </thead>
        <tbody>
          {trade.legs.map((leg) => (
            <LegRow
              key={leg.tradeLegId}
              leg={leg}
              busy={
                leg.status === 'CLOSING' ||
                (busy &&
                  (exitingLegId === null || exitingLegId === leg.tradeLegId))
              }
              locked={locked || busy || (drag?.moving ?? null) !== null}
              onExit={() => onExitLeg(trade, leg)}
              {...(onExitTicket
                ? {
                    onTicket: (anchor: HTMLElement) =>
                      onExitTicket(trade, leg, anchor),
                  }
                : {})}
              {...(drag
                ? { drag, onDragStart: () => drag.onDragStart(trade, leg) }
                : {})}
            />
          ))}
        </tbody>
      </table>

      {arming && !trade.premiumProtection && (
        <ProtectionPanel legs={trade.legs} onSaved={onArmed ?? (() => {})} />
      )}
    </section>
  )
}

/**
 * Chain clicks, one after another.
 *
 * A flat table rather than a card each, with the BROKER as its own column —
 * these rows come from several accounts and the account is part of reading
 * the row, not a property of a group heading.
 *
 * A manual trade with more than one leg is possible (a basket fired without
 * a saved strategy) and prints one row per leg, with the trade's name
 * repeated. They exit together only through the row's own button, which
 * exits that leg.
 */
export function ManualLegs({
  trades,
  onExitLeg,
  onExitTicket,
  onExitAll,
  busyFor,
  drag,
  grouping,
}: {
  trades: readonly OpenBookTrade[]
  onExitLeg: (trade: OpenBookTrade, leg: OpenBookLeg) => void
  /**
   * Open the exit TICKET for one leg — part of it, or at a price (5.15).
   *
   * Separate from `onExitLeg`, which is the row's × and still means "close
   * this leg now, at market". Two gestures, two meanings: the cross is the
   * fast one and must stay one click.
   */
  /**
   * `anchor` is the button that was pressed — 9.16. The ticket opens under
   * it rather than in the middle of the screen, and the element rather than
   * its rect because the rect has to be taken AFTER the dialog has mounted
   * and locked the page's scroll.
   */
  onExitTicket?: (
    trade: OpenBookTrade,
    leg: OpenBookLeg,
    anchor: HTMLElement,
  ) => void
  /**
   * Close every manual leg — the same gesture a basket's EXIT ALL is.
   *
   * It takes the TRADES rather than the legs because each chain click is a
   * Trade of its own: closing this block is closing N trades, and the
   * server has to be told which ones. Passing tokens instead would scope
   * its query to the whole account and close a strategy leg that shared one
   * of these strikes.
   */
  onExitAll?: (trades: readonly OpenBookTrade[]) => void
  /**
   * Is THIS trade's own exit in flight?
   *
   * There used to be a `locked` prop beside this saying "the whole block is
   * busy", which stopped being a single truth the day EXIT ALL became one
   * request per broker account: with legs on two accounts, one of them can
   * be mid-exit while the other has not been asked. Every lock below is
   * therefore derived per trade from this, and the block-wide flag is gone
   * rather than left to mean one of the two accounts.
   */
  busyFor: (trade: OpenBookTrade) => boolean
  /** 9.9. Present when a basket leg can be dragged back out to here. */
  drag?: LegDragApi
  /**
   * 9.26. Ticking loose legs so they can be named as one basket.
   *
   * Absent turns the whole affordance off — no boxes, no button — which is
   * what a read-only book wants.
   */
  grouping?: {
    selected: ReadonlySet<string>
    onToggle: (tradeLegId: string) => void
    onGroup: () => void
    busy: boolean
  }
}) {
  const rows = trades.flatMap((trade) =>
    trade.legs.map((leg) => ({ trade, leg })),
  )
  /*
   * Only trades that still HOLD something can be exited. A manual trade
   * whose leg failed is drawn here so the failure is visible, and including
   * it would ask the server to close nothing.
   */
  const held = trades.filter((t) => t.legs.some((l) => l.netQty !== 0))
  const heldLegCount = held.reduce(
    (n, t) => n + t.legs.filter((l) => l.netQty !== 0).length,
    0,
  )
  const anyBusy = held.some((t) => busyFor(t))
  /*
   * Disabled only when EVERY account's manual exit is in flight. Disabling on
   * `anyBusy` would be the cross-account lock again in a new place: with one
   * broker working and the other refused, the one button that can ask the
   * second broker would be greyed out. A second press is safe — `useExitOrder`
   * drops a duplicate on the same key before it leaves the browser.
   */
  const allBusy = held.every((t) => busyFor(t))
  const dragging = drag?.dragged ?? null
  const receiving = dragging !== null && canDetach(dragging)
  const [over, setOver] = useState(false)

  useEffect(() => {
    if (dragging === null) setOver(false)
  }, [dragging])

  /*
   * THE SECTION HAS TO EXIST TO BE DROPPED ON.
   *
   * It returns null when there is nothing manual — and the first leg a
   * trader pulls out of a basket is exactly the case where there is nothing
   * manual yet. So while a detachable leg is in the hand, the heading and an
   * empty target are drawn with nothing in them.
   */
  if (rows.length === 0 && !receiving) return null

  return (
    <section
      className="mt-1"
      data-testid="manual-legs"
      onDragOver={
        receiving
          ? (e) => {
              allowLegDrop(e)
              if (!over) setOver(true)
            }
          : undefined
      }
      onDragLeave={
        receiving
          ? (e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                setOver(false)
            }
          : undefined
      }
      onDrop={
        receiving
          ? (e) => {
              e.preventDefault()
              setOver(false)
              drag?.onDrop(null, 'Manual')
            }
          : undefined
      }
      data-droppable={receiving ? 'true' : undefined}
      data-dropover={receiving && over ? 'true' : undefined}
    >
      {/*
        EXIT ALL here, and only here. A basket's button says EXIT because it
        closes one position; this closes every loose leg in the block, which
        is the one place the word "all" is doing real work. Without it the
        only way out of eight chain clicks was eight crosses.
      */}
      <div className="mb-1.5 flex items-center gap-2 px-0.5">
        <span className="text-[10px] font-bold tracking-[0.18em] text-muted-foreground">
          PLACED FROM THE OPTION CHAIN
        </span>
        <span className="flex-1" />
        {/*
          ── 9.26. GROUP, BESIDE EXIT ALL AND BEFORE IT ───────────────────
          It appears only once two legs are ticked, so the header is not
          carrying a permanently disabled button for a gesture most visits
          never use. Before EXIT ALL because it is the harmless one of the
          two, and a destructive button should not be the one the hand
          travels past on its way to the safe one.
        */}
        {grouping && grouping.selected.size >= 2 && (
          <button
            type="button"
            onClick={grouping.onGroup}
            disabled={grouping.busy}
            data-testid="manual-group"
            title={`Make these ${grouping.selected.size} legs one named position`}
            className="flex h-5 items-center gap-1 rounded bg-primary/15 px-2 text-[9px] font-extrabold tracking-wide text-primary transition-all hover:bg-primary hover:text-white disabled:opacity-40"
          >
            <Layers size={9} />
            {grouping.busy ? 'GROUPING…' : `GROUP ${grouping.selected.size}`}
          </button>
        )}
        {onExitAll && held.length > 0 && (
          <button
            type="button"
            onClick={() => onExitAll(held)}
            disabled={allBusy}
            aria-busy={anyBusy}
            data-testid="manual-exit-all"
            title={`Close every manual leg — ${heldLegCount} leg${heldLegCount === 1 ? '' : 's'}`}
            className="h-5 rounded bg-destructive/15 px-2 text-[9px] font-extrabold tracking-wide text-destructive transition-all hover:bg-destructive hover:text-white disabled:opacity-40"
          >
            {anyBusy ? 'EXITING…' : 'EXIT ALL'}
          </button>
        )}
      </div>
      {rows.length === 0 && (
        <div
          className={cn(
            'rounded-[10px] border border-dashed px-3 py-4 text-center text-[11px] transition-colors',
            over
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border text-muted-foreground',
          )}
          data-testid="manual-drop-hint"
        >
          Drop here to take this leg out of its strategy
        </div>
      )}
      <div
        className={cn(
          /*
           * `overflow-x-auto`, not `hidden`. A drawer narrow enough that
           * even the hidden-broker layout does not fit used to CLIP the
           * right-hand columns, and the first casualty was the exit cross —
           * the row was still there and there was no way to close it.
           * A scrollbar is an ugly last resort; an unreachable exit is a
           * dangerous one.
           */
          'overflow-x-auto rounded-[10px] border transition-colors',
          rows.length === 0 && 'hidden',
          receiving && over
            ? 'border-primary ring-1 ring-primary/40'
            : 'border-border',
        )}
      >
        {/*
          ── PROPORTIONAL WHEN THERE IS ROOM, PRIORITISED WHEN THERE IS NOT
          ────────────────────────────────────────────────────────────────
          `table-fixed` with a share per column, so a wide drawer spreads
          the space across ALL of them instead of parking the numbers in
          the right-hand corner behind one greedy column. The five numeric
          columns carry the SAME share, which is what makes them read as a
          table rather than as five ragged edges.

          The shares only work while LEG's share is wider than a contract
          name — about 205px for "NIFTY 03NOV 20550 CE" plus the pill and
          the grip beside it. Below that the container queries in
          terminalRedesign.css take the broker column away and hand LEG its
          share, which is the whole reason those breakpoints are the
          numbers they are rather than numbers picked by eye.
        */}
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-[9px] tracking-wider text-muted-foreground">
              {/*
                BROKER | TIME | ENTRY | LEG | QTY | LTP | P&L, asked for on
                2026-11-01. The shares are unchanged and so are the class
                names, which is what matters: the breakpoints in
                terminalRedesign.css hide `.tr-col-broker` and restate
                `.tr-col-leg` by CLASS, not by position, so they survive the
                reorder untouched.

                First to go is still BROKER. The chip truncates inside its
                share until then, and a clipped account is still
                recognisable where a clipped strike is not.
              */}
              {/*
                ── 9.26. THE BOX LEADS THE ROW ─────────────────────────────
                It began inside the LEG cell to avoid disturbing these
                shares, and that put it in the MIDDLE of the row — against
                the contract, reading as decoration on the name rather than
                as a control for the line. The same mistake the grip made in
                2026-11-01 and for the same reason.

                ITS 3% COMES FROM THE FIVE NUMERIC COLUMNS, 11.6 -> 11, and
                deliberately NOT from LEG. LEG's share is the one with a
                floor — the contract name needs ~205px, which is what fixes
                every breakpoint below — so taking from it would have moved
                them all. The numerics hold "2,373.05" in 98px at the
                narrowest stage and can spare it.

                Never hidden at any width. A column that vanishes takes the
                only way of selecting a row with it.
              */}
              <th className="w-[3%] py-1.5 pl-2" />
              {/* The handle leads the row — see the note on the cell. */}
              <th className="tr-col-grip w-[3%] py-1.5 pl-2" />
              <th className="tr-col-broker w-[12%] py-1.5 text-left font-semibold">
                BROKER
              </th>
              <th className="w-[11%] whitespace-nowrap py-1.5 pl-2 text-right font-semibold">
                TIME
              </th>
              <th className="tr-col-leg w-[20%] py-1.5 pl-3 text-left font-semibold">
                LEG
              </th>
              <th className="w-[11%] whitespace-nowrap py-1.5 pl-2 text-right font-semibold">
                ENTRY
              </th>
              <th className="w-[11%] whitespace-nowrap py-1.5 pl-2 text-right font-semibold">
                QTY
              </th>
              <th className="w-[11%] whitespace-nowrap py-1.5 pl-2 text-right font-semibold">
                LTP
              </th>
              <th className="w-[11%] whitespace-nowrap py-1.5 pl-2 pr-1 text-right font-semibold">
                P&amp;L
              </th>
              {/* The exit cross. Never the column that gets pushed out: a
                  position you cannot close is the worst thing this table
                  can do — which is exactly what `w-[7%]` did below a
                  1,160px drawer. See 9.57 on the basket table above: a
                  fixed-size control needs a fixed-size column. */}
              <th className="w-[60px] py-1.5 pr-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map(({ trade, leg }) => (
              <ManualRow
                key={leg.tradeLegId}
                trade={trade}
                leg={leg}
                busy={busyFor(trade)}
                locked={busyFor(trade) || (drag?.moving ?? null) !== null}
                onExit={() => onExitLeg(trade, leg)}
                {...(onExitTicket
                  ? {
                      onTicket: (anchor: HTMLElement) =>
                        onExitTicket(trade, leg, anchor),
                    }
                  : {})}
                {...(drag
                  ? { drag, onDragStart: () => drag.onDragStart(trade, leg) }
                  : {})}
                {...(grouping && leg.netQty !== 0 && leg.status === 'FILLED'
                  ? {
                      select: {
                        checked: grouping.selected.has(leg.tradeLegId),
                        onToggle: () => grouping.onToggle(leg.tradeLegId),
                      },
                    }
                  : {})}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

const ManualRow = memo(function ManualRow({
  trade,
  leg,
  onExit,
  onTicket,
  busy,
  locked = false,
  drag,
  onDragStart,
  select,
}: {
  onTicket?: (anchor: HTMLElement) => void
  trade: OpenBookTrade
  leg: OpenBookLeg
  onExit: () => void
  busy: boolean
  locked?: boolean
  drag?: LegDragApi
  onDragStart?: () => void
  /** 9.26. Absent on a leg that cannot join a group — see `useLegGrouping`. */
  select?: { checked: boolean; onToggle: () => void }
}) {
  const { ltp, pnl } = useLegMark(leg)
  const held = leg.netQty !== 0
  const closing = useClosing(leg.tradeLegId)
  const note = closing ? LEG_STATUS.CLOSING : LEG_STATUS[leg.status]

  const draggable = Boolean(drag) && held && !busy && !locked
  const inHand = drag?.dragged?.tradeLegId === leg.tradeLegId

  return (
    <tr
      draggable={draggable}
      onDragStart={
        draggable && onDragStart
          ? (e) => beginLegDrag(e, leg.tradeLegId, onDragStart)
          : undefined
      }
      onDragEnd={draggable ? drag?.onDragEnd : undefined}
      /*
       * The row IS the checkbox — see the cell. Anything inside
       * `[data-no-row-select]` is exempt, which is the actions cell: a
       * click aimed at the exit cross must not also tick the row on its
       * way past, and a mis-hit beside the cross should do nothing rather
       * than silently select.
       */
      onClick={
        select && !busy && !locked
          ? (e) => {
              if ((e.target as HTMLElement).closest('[data-no-row-select]'))
                return
              select.onToggle()
            }
          : undefined
      }
      className={cn(
        'group/leg border-b border-border/40 text-[11px] last:border-0',
        !held && 'opacity-70',
        inHand && 'opacity-40',
        select && !busy && !locked && 'cursor-pointer',
        // The ticked row is tinted, so a selection of six is read down the
        // block rather than by checking six small boxes.
        select?.checked && 'bg-primary/[0.07]',
      )}
      data-testid={`trade-leg-${leg.tradeLegId}`}
    >
      {/* `overflow-hidden` so a long account name is clipped by its own
          column rather than printed across the next one. */}
      {/*
        ── THE GRIP LEADS THE ROW — ROADMAP 9.9 ─────────────────────────────
        It lived inside the LEG cell, which was correct while LEG was the
        first column. The 2026-11-01 reorder put LEG third and the dots
        landed in the MIDDLE of the row, against the strike, reading as
        decoration on the contract rather than as a handle for the line.

        A column of its own is the only placement that survives a reorder.
        It holds its width at all times so nothing shifts sideways when the
        pointer enters — a column that moves under the cursor is how a
        mis-drag starts — and it is still the first thing shed below 540px.
      */}
      {/*
        ── 9.26. THE BOX, AND THE WHOLE ROW AS ITS TARGET ─────────────────
        A 12px checkbox is a poor thing to ask a trader to hit while a
        market is moving, so the ROW is the control and the box is only its
        readout. The actions cell opts out — a click meant for the exit
        cross must never also tick a box — and the box stops its own click
        from reaching the row, or the two handlers would cancel out.
      */}
      <td className="py-1.5 pl-2">
        {select && (
          <input
            type="checkbox"
            checked={select.checked}
            onChange={select.onToggle}
            onClick={(e) => e.stopPropagation()}
            disabled={busy || locked}
            aria-label={`Select ${leg.symbol} for grouping`}
            data-testid={`select-leg-${leg.tradeLegId}`}
            className="h-3 w-3 cursor-pointer accent-[hsl(var(--primary))] disabled:cursor-not-allowed disabled:opacity-40"
          />
        )}
      </td>
      <td className="tr-col-grip py-1.5 pl-2">
        {drag && (
          <span
            className={cn(
              'flex items-center transition-opacity',
              draggable
                ? 'cursor-grab text-muted-foreground opacity-0 group-hover/leg:opacity-60'
                : 'opacity-0',
            )}
            title={
              draggable
                ? 'Drag onto a strategy to add this leg to it'
                : undefined
            }
            aria-hidden
          >
            <GripVertical size={11} />
          </span>
        )}
      </td>
      <td className="tr-col-broker overflow-hidden py-1.5 pr-1">
        <BrokerChip label={trade.broker.label} />
        <PremiumProtectionBadge value={trade.premiumProtection} />
      </td>
      <td
        className="num whitespace-nowrap pl-2 text-right text-muted-foreground"
        title="When this leg filled, IST"
      >
        {fillTime(leg.lastFillAt)}
      </td>
      {/* `min-w-0` so `Contract`'s own truncation is reachable. It should
          never fire — the breakpoints exist so this column is always wider
          than a contract name — but an ellipsis is a better failure than
          the name running out over QTY. */}
      <td className="py-1.5 pl-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <SidePill side={leg.side} />
          <Contract leg={leg} />
          {note && (
            <span
              className={cn(
                'shrink-0 text-[8.5px] font-bold uppercase tracking-wide',
                note.cls,
              )}
            >
              {note.text}
            </span>
          )}
          <LegFlagBadge leg={leg} />
          {drag && (
            <LegRoleChip
              /*
               * 9.58. After TIME, the chip is what carries the row from
               * 640 down to 560 — it sits INSIDE the LEG cell, so losing
               * its ~40px lowers that cell's floor rather than competing
               * with it. `tr-col-role` already drops it at 540 for the
               * manual table; the basket table needs it sooner because it
               * carries a TIME column the manual one does not.
               */
              className="tr-col-role @max-[640px]/book:hidden"
              role={leg.role}
              disabled={locked}
              testId={`leg-role-chip-${leg.tradeLegId}`}
              onChange={(role) => drag.onRole(leg.tradeLegId, role)}
            />
          )}
        </span>
      </td>
      {/* Every numeric cell is nowrap, so none of them can be the one that
          gives way — the broker column is, by construction. */}
      <td className="num whitespace-nowrap pl-2 text-right text-muted-foreground">
        {held ? leg.avgPrice.toFixed(2) : '—'}
      </td>
      <td className="num whitespace-nowrap pl-2 text-right text-muted-foreground">
        {signedQty(leg)}
      </td>
      <td className="num whitespace-nowrap pl-2 text-right font-semibold">
        {ltp === null ? '—' : ltp.toFixed(2)}
      </td>
      <td className="whitespace-nowrap pl-2 pr-1 text-right">
        <Money value={pnl} />
      </td>
      {/*
        ── TWO GESTURES, AND THE CROSS STAYS ONE CLICK — ROADMAP 5.15 ──────
        The × is "close this leg, now, at market" and must not grow a step.
        The sliders open a TICKET: part of the leg, or an order that rests.
        Same icon the chain's limit button uses, because it opens the same
        kind of thing and a trader should not have to learn two glyphs.

        Both sit in the column that is never shed. An overflowing row has
        pushed an exit control off this table twice; context goes first,
        controls never.
      */}
      {/* The action buttons. Opted out of row selection — see the <tr>. */}
      <td className="whitespace-nowrap pr-2 text-right" data-no-row-select>
        <span className="inline-flex items-center justify-end gap-0.5">
          {onTicket && (
            <button
              type="button"
              onClick={(e) => onTicket(e.currentTarget)}
              disabled={busy || locked || !held}
              aria-label={`Exit part of ${leg.symbol}, or at a price`}
              data-testid={`exit-ticket-${leg.tradeLegId}`}
              title={
                held
                  ? 'Exit part of this leg, or at a price'
                  : 'This leg holds nothing'
              }
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-all hover:bg-primary/10 hover:text-primary disabled:opacity-30"
            >
              <SlidersHorizontal size={11} />
            </button>
          )}
          <button
            type="button"
            onClick={onExit}
            disabled={busy || locked || !held}
            aria-label={busy ? `Exiting ${leg.symbol}` : `Exit ${leg.symbol}`}
            aria-busy={busy}
            data-testid={`exit-leg-${leg.tradeLegId}`}
            title={held ? 'Exit this leg at market' : 'This leg holds nothing'}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-all hover:bg-destructive/10 hover:text-destructive disabled:opacity-30"
          >
            {busy ? (
              <LoaderCircle size={12} className="animate-spin" />
            ) : (
              <X size={12} />
            )}
          </button>
        </span>
      </td>
    </tr>
  )
})
